import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { PermitStatusService } from '../../services/permit-status.service';
import { MessageService } from '../../../global/services/message/message.abstract.service';
import { PermitStatus, CreateStatusPayload, UpdateStatusConfigPayload } from '../../models/permit-status.model';
import { PermitStatusType } from '../../constants/permit.constants';

interface SelectOption {
    value: string;
    label: string;
}

@Component({
    selector: 'app-permit-status-form',
    templateUrl: './permit-status-form.component.html',
    styleUrls: ['./permit-status-form.component.scss'],
})
export class PermitStatusFormComponent implements OnInit, OnDestroy, OnChanges {
    @Input() statusToEdit: PermitStatus | null = null;

    @Output() saved = new EventEmitter<void>();
    @Output() cancel = new EventEmitter<void>();

    form: FormGroup;
    isSaving = false;
    statusTypeOptions: SelectOption[] = [];

    private destroy$ = new Subject<void>();

    constructor(
        private fb: FormBuilder,
        private translate: TranslateService,
        private statusService: PermitStatusService,
        private messageService: MessageService,
    ) {}

    ngOnInit(): void {
        this.buildStatusTypeOptions();
        this.initForm();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.statusToEdit && this.form) {
            this.patchForm();
        }
    }

    private buildStatusTypeOptions(): void {
        this.statusTypeOptions = Object.values(PermitStatusType).map((type) => ({
            value: type,
            label: this.translate.instant(`permit.config.statuses.type_${type}`),
        }));
    }

    private initForm(): void {
        const s = this.statusToEdit;
        this.form = this.fb.group({
            nameFr: [s?.name?.fr || '', Validators.required],
            nameEn: [s?.name?.en || '', Validators.required],
            color: [s?.color || '#2196f3', Validators.required],
            type: [s?.type || PermitStatusType.UNDER_REVIEW, Validators.required],
            terminal: [s?.terminal ?? false],
        });
    }

    private patchForm(): void {
        const s = this.statusToEdit;
        this.form.patchValue({
            nameFr: s?.name?.fr || '',
            nameEn: s?.name?.en || '',
            color: s?.color || '#2196f3',
            type: s?.type || PermitStatusType.UNDER_REVIEW,
            terminal: s?.terminal ?? false,
        });
    }

    get isEdit(): boolean {
        return !!this.statusToEdit;
    }

    onSave(): void {
        this.form.markAllAsTouched();
        if (this.form.invalid || this.isSaving) return;

        this.isSaving = true;
        const v = this.form.value;
        const payload: CreateStatusPayload = {
            name: { fr: v.nameFr.trim(), en: v.nameEn.trim() },
            color: v.color,
            type: v.type,
            terminal: v.terminal,
        };

        const request$ = this.statusToEdit ? this.statusService.updateStatus(this.statusToEdit._id, payload as UpdateStatusConfigPayload) : this.statusService.createStatus(payload);

        request$.pipe(takeUntil(this.destroy$)).subscribe({
            next: () => {
                this.isSaving = false;
                const key = this.isEdit ? 'permit.config.statuses.form_saved_edit' : 'permit.config.statuses.form_saved_create';
                this.messageService.success(this.translate.instant(key));
                this.saved.emit();
            },
            error: () => {
                this.isSaving = false;
                this.messageService.error(this.translate.instant('permit.config.common.error'));
            },
        });
    }

    onCancel(): void {
        this.cancel.emit();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
